import React, { Fragment, useState, useEffect, useContext } from 'react';
import { MdRssFeed, MdImage, MdInfo } from 'react-icons/md';
import { TiWeatherPartlySunny } from 'react-icons/ti';
import { FaWarehouse } from 'react-icons/fa';
import { IoMdDocument } from 'react-icons/io';

import { ComponentContext } from '../../context/componentContext';
import { AuthContext } from '../../context/authContext';
import { FarmContext } from '../../context/farmContext';

import UnexpectedError from '../../components/UnexpectedError';
import DynamicSections from '../../components/Farm/Sections/DynamicSections';
import FarmElement from '../../components/helpers/FarmElement';

import Illustration from '../../images/environment.png';
import ActivityProgressBar from '../../components/Farm/ActivityProgressBar';

const Farm = ({ location: { state }, match: { params } }) => {
  document.title = `CrowdFarmer | Farms | ${params.id}`;

  const { menuOpen, reload, unexpectedError, setUnexpectedError, setLoading } = useContext(
    ComponentContext
  );
  const { session, setSession } = useContext(AuthContext);
  const { fetchFarm, fetchActivities, fetchFeeds } = useContext(FarmContext);

  const [view, setView] = useState('compA');
  const [farm, setFarm] = useState(null);
  const [activities, setActivities] = useState(null);
  const [feeds, setFeeds] = useState(null);
  const [dynamicData, setDynamicData] = useState(null);

  useEffect(() => {
    let mounted = true;
    if (session) {
      if (!state) {
        (async () => {
          setLoading(true);
          try {
            let farm = await fetchFarm(params.id);
            if (farm.code === 200) {
              let activities = await fetchActivities(farm.data._id, farm.data.product._id);
              if (activities.code === 200) {
                let feeds = await fetchFeeds(farm.data._id, farm.product);
                if (feeds.code === 200) {
                  if (mounted) {
                    setFarm(farm.data);
                    setActivities(activities.data);
                    setFeeds(feeds.data);
                    setDynamicData({
                      farm: farm.data,
                      feeds: feeds.data,
                      activities: activities.data
                    });
                  }
                } else setUnexpectedError('Unexpected Server Error!');
              } else setUnexpectedError('Unexpected Server Error!');
            } else setUnexpectedError('Unexpected Server Error!');
          } catch (error) {
            if (error && error.status === 401) setSession(false);
            else setUnexpectedError('Unexpected Network Error!');
          }
          setLoading(false);
        })();
      } else {
        (async () => {
          setLoading(true);
          try {
            let feeds = await fetchFeeds(state.data._id, state.data.product._id);
            if (mounted)
              if (feeds.code === 200) {
                setFeeds(feeds.data);
                setFarm(state.data);
                setActivities(state.activities);
                setDynamicData({
                  farm: state.data,
                  feeds: feeds.data,
                  activities: state.activities
                });
              } else setUnexpectedError('Unexpected Server Error!');
          } catch (error) {
            if (error && error.status === 401) setSession(false);
            else setUnexpectedError('Unexpected Network Error!');
          }
          setLoading(false);
        })();
      }
    }
    return () => (mounted = false);
  }, [
    reload,
    session,
    setLoading,
    fetchFarm,
    params._id,
    setUnexpectedError,
    setSession,
    fetchActivities,
    fetchFeeds,
    state,
    params.id
  ]);

  return (
    <>
      {unexpectedError ? (
        <UnexpectedError unexpectedError={unexpectedError} />
      ) : (
        <Fragment>
          <div className="relative pt-32 bg-cf-400 w-screen">
            {farm && activities && (
              <div className="absolute hidden md:block right-0 bg-white rounded-t-lg p-6 mr-10 w-2/5 mt-12">
                <div className="ml-4">
                  <h4 className="text-sm md:text-base lg:text-lg font-bold">
                    {farm.name} | {farm.acreage} Acre(s)
                  </h4>
                  <p className="text-xs md:text-sm lg:text-base">
                    {farm.product.location.name}, {farm.product.location.state}
                  </p>
                </div>

                <ActivityProgressBar
                  farmId={farm._id}
                  activities={activities}
                  duration={farm.product.duration}
                  startDate={farm.product.startDate}
                  status={farm.status}
                />
              </div>
            )}
            <img src={Illustration} alt="Maize" className="w-full object-center" />
          </div>
          <Fragment>
            <div className="container mx-auto bg-cf-200 w-screen">
              <div className={`flex flex-auto justify-center ${menuOpen ? 'pl-8 md:pl-24' : ''}`}>
                <div className="w-full md:w-4/5 lg:w-133 -mt-0">
                  <div className="grid md:grid-cols-6 divide-x divide-gray-400  bg-white rounded items-center">
                    <FarmElement
                      view={view === 'compA'}
                      title="Farm Feed"
                      icon={<MdRssFeed size="16px" />}
                      click={() => {
                        setView('compA');
                        setDynamicData({ farm, feeds, activities });
                      }}
                    />
                    <FarmElement
                      view={view === 'compB'}
                      title="Farm Gallery"
                      icon={<MdImage size="16px" />}
                      click={() => {
                        setView('compB');
                        setDynamicData({ crop: farm.product.crop, feeds });
                      }}
                    />
                    <FarmElement
                      view={view === 'compC'}
                      title="Farm Conditions"
                      icon={<TiWeatherPartlySunny size="16px" />}
                      click={() => {
                        setView('compC');
                        setDynamicData({ farm, feeds, activities });
                      }}
                    />
                    <FarmElement
                      view={view === 'compD'}
                      title="Farm Info"
                      icon={<MdInfo size="16px" />}
                      click={() => {
                        setView('compD');
                        setDynamicData(farm);
                      }}
                    />

                    <FarmElement
                      view={view === 'compE'}
                      title="Documents"
                      icon={<IoMdDocument size="16px" />}
                      click={() => {
                        setView('compE');
                        setDynamicData({ farm, feeds });
                      }}
                    />

                    <FarmElement
                      view={view === 'compF'}
                      title="Warehouse"
                      icon={<FaWarehouse size="16px" />}
                      click={() => setView('compF')}
                    />
                  </div>
                </div>
              </div>

              <DynamicSections view={view} data={dynamicData} />
            </div>
          </Fragment>
        </Fragment>
      )}
    </>
  );
};

export default Farm;
